import { STATUS_STYLES } from "@/lib/constants";
import { GripVertical, AlertTriangle, UserRound, PackageX } from "lucide-react";
import { LANE_H, fmtDuration } from "./ganttUtils";

function roundedClass(geo) {
  if (geo.clipL) return "rounded-r-md";
  if (geo.clipR) return "rounded-l-md";
  return "rounded-md";
}

export function OrderBar({ order: o, geo, lane, canDrag, remoteDrag, shortage, onDragPresence, onSelect }) {
  const s = STATUS_STYLES[o.status] || STATUS_STYLES.Pending;
  const dur = fmtDuration(o);
  const draggable = canDrag && !remoteDrag;
  const narrow = geo.width < 7; // short bars: drop chrome so the order code stays readable
  const pct = o.target_quantity > 0 ? Math.min(100, Math.round(((o.produced_quantity || 0) / o.target_quantity) * 100)) : 0;

  const title = [
    `${o.order_code} · ${o.product_name} · ${o.status}${dur ? ` · est. ${dur}` : ""} · ${pct}% produced (${o.produced_quantity || 0}/${o.target_quantity})`,
    o.has_conflict ? `⚠ ${o.conflict_reason}` : null,
    shortage?.length ? `⛔ Material shortage: ${shortage.map((m) => `${m.sku} short ${m.missing.toLocaleString("en-IN")} ${m.unit}`).join(", ")}` : null,
    remoteDrag ? `✋ ${remoteDrag.user} is moving this order` : null,
  ].filter(Boolean).join("\n");

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("orderId", o.id);
        onDragPresence?.(o.id, true);
      }}
      onDragEnd={() => onDragPresence?.(o.id, false)}
      onClick={() => onSelect?.(o)}
      data-testid="gantt-order-bar"
      data-order-code={o.order_code}
      className={`absolute flex items-center gap-1 ${narrow ? "px-1" : "px-2"} text-white text-[11px] shadow-sm overflow-hidden ${
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-default"
      } ${roundedClass(geo)} ${
        o.has_conflict ? "ring-2 ring-amber-400 ring-offset-1" : ""
      } ${remoteDrag ? "ring-2 ring-violet-500 ring-offset-1 animate-pulse" : ""}`}
      style={{ left: `${geo.left}%`, width: `${geo.width}%`, top: lane * LANE_H + 7, height: LANE_H - 8, background: s.bar }}
      title={title}
    >
      {pct > 0 && (
        <span className="absolute bottom-0 left-0 h-[3px] bg-white/90 rounded-r-full pointer-events-none" style={{ width: `${pct}%` }} data-testid="gantt-progress-strip" />
      )}
      {remoteDrag && <UserRound className="h-3 w-3 text-violet-200 shrink-0" data-testid="gantt-remote-drag-icon" />}
      {o.has_conflict && <AlertTriangle className="h-3 w-3 text-amber-200 shrink-0" data-testid="gantt-conflict-icon" />}
      {shortage?.length > 0 && (
        <span className="flex items-center justify-center h-4 w-4 rounded-full bg-red-600 ring-1 ring-white/70 shrink-0" data-testid="gantt-shortage-badge">
          <PackageX className="h-2.5 w-2.5 text-white" />
        </span>
      )}
      {draggable && !o.has_conflict && !remoteDrag && !narrow && <GripVertical className="h-3 w-3 opacity-70 shrink-0" />}
      <span className="font-mono-data font-semibold whitespace-nowrap">{o.order_code}</span>
      {!narrow && <span className="truncate hidden sm:inline">· {remoteDrag ? `${remoteDrag.user} is moving…` : o.product_name}</span>}
      {dur && !narrow && <span className="ml-auto font-mono-data opacity-80 whitespace-nowrap hidden md:inline">{dur}</span>}
    </div>
  );
}
